import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Chip, Skeleton, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import type { ProductType } from '@/api/models';
import { BranchScopeField } from '../productFilters/BranchScopeField';
import { PRODUCT_TYPE_LABEL_KEY } from './productDetailMeta';
import { asHeading2 } from './productDetailTypography';
import type { ProductDetailBranch } from './useProductDetail';

type ProductDetailHeaderProps = {
    /** The product's name and type; `null` while the product loads. */
    product: { name: string; type: ProductType } | null;
    /** Whose stock the page counts, and the control that picks it. */
    branch: ProductDetailBranch;
    /** A Draft chip beside the type while the product is a draft (bos-app#225). */
    isDraft: boolean;
    onEdit: () => void;
};

/**
 * The product details page's header row (design board **ProductDetail**):
 * the name and type chip (and a Draft chip on a draft), then "Stock for" beside Edit (bos-app#119). The
 * two actions wrap together onto their own line on a narrow screen.
 *
 * Edit and "Stock for" come from the route and the branch scope, not from the
 * product, so they are there and usable while the product loads; only the
 * name and the chip wait for it, as placeholders in their place (rulebook
 * §5.5, §12; bos-app#139).
 */
export const ProductDetailHeader: React.FC<ProductDetailHeaderProps> = ({
    product,
    branch,
    isDraft,
    onEdit,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="flex flex-wrap items-center gap-3">
            {product ? (
                <>
                    {/* An h2 under the layout's page title, at the design's
                        24px heading size. */}
                    <Typography
                        className="wrap-anywhere"
                        render={asHeading2}
                        type="h3"
                        {...getNodeTestIdProp('product-detail-name')}
                    >
                        {product.name}
                    </Typography>
                    <Chip
                        color={
                            product.type === 'Variable' ? 'accent' : 'default'
                        }
                        size="sm"
                        variant="soft"
                        {...getNodeTestIdProp('product-detail-type')}
                    >
                        <Chip.Label>
                            {t(PRODUCT_TYPE_LABEL_KEY[product.type])}
                        </Chip.Label>
                    </Chip>
                    {isDraft ? (
                        <Chip
                            color="warning"
                            size="sm"
                            variant="soft"
                            {...getNodeTestIdProp('product-detail-draft-chip')}
                        >
                            <Chip.Label>
                                {t('products.detail.draft.chip')}
                            </Chip.Label>
                        </Chip>
                    ) : null}
                </>
            ) : (
                <div
                    className="flex items-center gap-3"
                    {...getNodeTestIdProp('product-detail-header-loading')}
                >
                    <Skeleton className="h-8 w-64 rounded-lg" />
                    <Skeleton className="h-5 w-16 rounded-md" />
                </div>
            )}
            <div className="ml-auto flex flex-wrap items-center gap-3">
                <BranchScopeField
                    copy={{ label: t('products.detail.stockScope.label') }}
                    layout="inline"
                    onChange={branch.onChange}
                    scope={branch.scope}
                    value={branch.branchId}
                />
                <Button
                    onPress={onEdit}
                    variant="outline"
                    {...getNodeTestIdProp('product-detail-edit')}
                >
                    <Icon icon="gravity-ui:pencil" />
                    {t('products.detail.edit')}
                </Button>
            </div>
        </div>
    );
};
